/**
 * Centralized auth-session refresh for axios + fetch clients.
 * Uses the existing HttpOnly session cookie (credentials: include) to mint a new JWT.
 */

import { AUTH_TOKEN_KEY, clearLocalAuthState, getApiUrl, isPublicAuthRequestUrl } from '../utils/authUtils';

const API = getApiUrl();
export const AUTH_REFRESHED_EVENT = 'fundelite:auth-refreshed';
export const AUTH_SESSION_EXPIRED_EVENT = 'fundelite:auth-session-expired';

let refreshPromise = null;

export function getStoredAccessToken() {
  return (
    localStorage.getItem(AUTH_TOKEN_KEY)
    || localStorage.getItem('token')
    || localStorage.getItem('access_token')
    || localStorage.getItem('authToken')
  );
}

export function persistAccessToken(token) {
  if (!token) return;
  localStorage.setItem(AUTH_TOKEN_KEY, token);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_REFRESHED_EVENT, { detail: { token } }));
  }
}

export function shouldAttemptAuthRefresh(url, { retried = false } = {}) {
  if (retried) return false;
  const u = String(url || '');
  if (!u) return false;
  if (isPublicAuthRequestUrl(u)) return false;
  if (u.includes('/auth/refresh')) return false;
  if (u.includes('/auth/logout')) return false;
  if (u.includes('/auth/login')) return false;
  if (u.includes('/auth/signup')) return false;
  return true;
}

/**
 * Single-flight refresh. Concurrent callers share one in-flight request.
 * Returns the new access token string, or null if refresh failed.
 */
export async function refreshAuthSession() {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        return null;
      }

      const token = data?.token || data?.access_token || null;
      if (!token) {
        return null;
      }

      persistAccessToken(token);
      return token;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export function clearAuthOnRefreshFailure() {
  clearLocalAuthState();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
  }
}

/**
 * Record return path and navigate to login once.
 */
export function redirectToLoginForExpiredSession(returnPath) {
  if (typeof window === 'undefined') return;

  const path = returnPath
    || `${window.location.pathname}${window.location.search}${window.location.hash}`;

  try {
    sessionStorage.setItem('funding-hq:auth-return', path);
  } catch {
    // ignore
  }

  const loginUrl = `/login?redirect=${encodeURIComponent(path)}`;
  const current = `${window.location.pathname}${window.location.search}`;
  if (current.startsWith('/login')) {
    return;
  }

  window.location.assign(loginUrl);
}

export function consumeAuthReturnPath() {
  try {
    const path = sessionStorage.getItem('funding-hq:auth-return');
    if (path) sessionStorage.removeItem('funding-hq:auth-return');
    return path || null;
  } catch {
    return null;
  }
}
